import React, {useMemo, useState} from "react";
import {Controller, FieldError, useFormContext} from "react-hook-form";
import {InputAdornment, ListSubheader} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {Box, Colors, Container, FormControl, IconButton, IconSearch, IconTrash, Text} from "@bounce/shared/ui";
import {InputUtils} from "@bounce/shared/helpers";
import {MenuItem, Select, TextField} from "../../../ui/components";
import {ParameterInfoTooltip} from "../../../components/tooltips";
import {ParamSchema} from "../../../apis/requests/segments/types";
import {useSearchCurrentCreditors} from "../../../apis/requests/segments/searchCurrentCreditors";
import {useSearchProducts} from "../../../apis/requests/segments/searchProducts";
import {
  CURRENCY_PARAMETERS,
  CURRENT_CREDITOR_PARAMETER,
  FEATURE_FLAG_PARAMETER,
  FRACTION_PARAMETERS,
  MULTI_VALUE_OPERATORS,
  NO_VALUE_OPERATORS,
  OPERATOR_LABELS,
  PRODUCT_PARAMETER,
} from "./constants";
import {defaultValueForCondition, findParam} from "./helpers";
import {ConditionsFormValues} from "./schema";
import EnumValueInput from "./value-inputs/EnumValueInput";
import FlagValueInput from "./value-inputs/FlagValueInput";
import MultiTextValueInput from "./value-inputs/MultiTextValueInput";
import NumericValueInput from "./value-inputs/NumericValueInput";
import SearchValueInput from "./value-inputs/SearchValueInput";
import TextValueInput from "./value-inputs/TextValueInput";

type Props = {
  groupIndex: number;
  index: number;
  parameters: ParamSchema[];
  onRemove: () => void;
  showLabels?: boolean;
};

const PARAMETER_SEARCH_MIN_OPTIONS = 10;

const SEARCHABLE_PARAMETERS: Record<string, {useSearch: typeof useSearchProducts; pluralNoun: string}> = {
  [PRODUCT_PARAMETER]: {useSearch: useSearchProducts, pluralNoun: "products"},
  [CURRENT_CREDITOR_PARAMETER]: {useSearch: useSearchCurrentCreditors, pluralNoun: "creditors"},
};

const parameterLabel = (parameter: string) => InputUtils.prettierSnakeCase(parameter);

// On focus, use a 1px outline matching the "+ Add rule" (outlined-primary) button
// so every field in the row reads as one set. Resting borders keep the default.
const FIELD_OUTLINE_COLOR = alpha(Colors.blue._800, 0.5);
const focusedOutline = {
  borderColor: `${FIELD_OUTLINE_COLOR} !important`,
  borderWidth: "1px !important",
};
const fieldOutlineSx = {
  // Descendant form (row Container) and self form (InputSx on an input root).
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": focusedOutline,
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": focusedOutline,
};

const ConditionRow: React.FC<Props> = ({groupIndex, index, parameters, onRemove, showLabels = true}) => {
  const {
    control,
    watch,
    setValue,
    formState: {errors, isSubmitted},
  } = useFormContext<ConditionsFormValues>();

  const [parameterSearch, setParameterSearch] = useState("");
  const showParameterSearch = parameters.length > PARAMETER_SEARCH_MIN_OPTIONS;
  const visibleParameters = useMemo(() => {
    const q = parameterSearch.trim().toLowerCase();
    if (!q) return parameters;
    return parameters.filter((p) => parameterLabel(p.parameter).toLowerCase().includes(q));
  }, [parameters, parameterSearch]);

  const basePath: `groups.${number}.conditions.${number}` = `groups.${groupIndex}.conditions.${index}`;
  const parameter = watch(`${basePath}.parameter`);
  const operator = watch(`${basePath}.operator`);
  const value = watch(`${basePath}.value`);

  const param = findParam(parameters, parameter);
  const operatorOptions = param?.operators ?? [];
  const noValue = NO_VALUE_OPERATORS.has(operator);
  const multi = MULTI_VALUE_OPERATORS.has(operator);

  const rowErrors = errors.groups?.[groupIndex]?.conditions?.[index];
  const parameterError = rowErrors?.parameter as FieldError | undefined;
  const operatorError = rowErrors?.operator as FieldError | undefined;
  const valueError = rowErrors?.value as FieldError | undefined;

  const setValueOpts = isSubmitted
    ? ({shouldDirty: true, shouldValidate: true} as const)
    : ({shouldDirty: true} as const);

  const updateValue = (next: unknown) => setValue(`${basePath}.value`, next as never, setValueOpts);

  const onParameterChange = (next: string) => {
    const nextParam = findParam(parameters, next);
    const nextOperator = next === FEATURE_FLAG_PARAMETER ? "equals" : nextParam?.operators[0] ?? "";
    setValue(`${basePath}.parameter`, next, setValueOpts);
    setValue(`${basePath}.operator`, nextOperator, setValueOpts);
    updateValue(defaultValueForCondition(nextParam, nextOperator));
  };

  const onOperatorChange = (next: string) => {
    setValue(`${basePath}.operator`, next, setValueOpts);
    updateValue(defaultValueForCondition(param, next));
  };

  const renderValue = () => {
    if (noValue) return <Box sx={{flex: 2}} />;
    const searchable = param ? SEARCHABLE_PARAMETERS[param.parameter] : undefined;
    if (searchable) {
      return (
        <SearchValueInput
          key={param?.parameter}
          value={value}
          valueError={valueError}
          onChange={updateValue}
          showLabel={showLabels}
          useSearch={searchable.useSearch}
          pluralNoun={searchable.pluralNoun}
        />
      );
    }
    const optionsList = param?.options;
    if (optionsList && optionsList.length > 0) {
      if (param?.parameter === FEATURE_FLAG_PARAMETER) {
        return (
          <FlagValueInput
            value={value}
            valueError={valueError}
            onChange={updateValue}
            flagOptions={optionsList}
            multi={multi}
            showLabel={showLabels}
          />
        );
      }
      return (
        <EnumValueInput
          value={value}
          valueError={valueError}
          onChange={updateValue}
          options={optionsList}
          multi={multi}
          showLabel={showLabels}
        />
      );
    }
    if (multi) {
      return (
        <MultiTextValueInput value={value} valueError={valueError} onChange={updateValue} showLabel={showLabels} />
      );
    }
    if (param?.kind === "numeric") {
      return (
        <NumericValueInput
          value={value}
          valueError={valueError}
          onChange={updateValue}
          isFraction={FRACTION_PARAMETERS.has(param.parameter)}
          isBalance={CURRENCY_PARAMETERS.has(param.parameter)}
          showLabel={showLabels}
        />
      );
    }
    return <TextValueInput value={value} valueError={valueError} onChange={updateValue} showLabel={showLabels} />;
  };

  return (
    <Container row fullWidth sx={{gap: 1, alignItems: "flex-start", ...fieldOutlineSx}}>
      <Box sx={{width: 200}}>
        {showLabels && (
          <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
            Parameter
          </Text>
        )}
        <Controller
          name={`${basePath}.parameter`}
          control={control}
          render={({field}) => (
            <FormControl size="small" fullWidth error={!!parameterError}>
              <Select
                {...field}
                fullWidth
                displayEmpty
                onChange={(e) => onParameterChange(e.target.value as string)}
                renderValue={(selected) => (selected ? parameterLabel(selected as string) : "")}
                MenuProps={{PaperProps: {style: {maxHeight: 400}}, autoFocus: false}}
              >
                {showParameterSearch && (
                  <ListSubheader sx={{py: 1, bgcolor: "background.paper"}}>
                    <TextField
                      autoFocus
                      size="small"
                      placeholder="Search…"
                      fullWidth
                      value={parameterSearch}
                      onChange={(e) => setParameterSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputSx={fieldOutlineSx}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <IconSearch />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </ListSubheader>
                )}
                {visibleParameters.map((p) => (
                  <MenuItem key={p.parameter} value={p.parameter}>
                    <Box sx={{display: "flex", alignItems: "center", width: "100%", gap: 1}}>
                      <Box sx={{flex: 1, minWidth: 0}}>{parameterLabel(p.parameter)}</Box>
                      <ParameterInfoTooltip parameter={p.parameter} />
                    </Box>
                  </MenuItem>
                ))}
                {showParameterSearch && visibleParameters.length === 0 && (
                  <MenuItem disabled value="__no_match__">
                    No matches
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        />
      </Box>
      {parameter !== FEATURE_FLAG_PARAMETER && (
        <Box sx={{flex: 1}}>
          {showLabels && (
            <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
              Operator
            </Text>
          )}
          <Controller
            name={`${basePath}.operator`}
            control={control}
            render={({field}) => (
              <FormControl size="small" fullWidth disabled={!parameter} error={!!operatorError}>
                <Select {...field} fullWidth displayEmpty onChange={(e) => onOperatorChange(e.target.value as string)}>
                  {operatorOptions.map((op) => (
                    <MenuItem key={op} value={op}>
                      {OPERATOR_LABELS[op] ?? op}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Box>
      )}
      {renderValue()}
      <Box>
        {showLabels && (
          <Text size={12} sx={{mb: 0.5, visibility: "hidden"}}>
            &nbsp;
          </Text>
        )}
        <IconButton
          size="small"
          onClick={onRemove}
          aria-label="Remove condition"
          sx={{
            border: `1px solid ${Colors.tertiary._300}`,
            borderRadius: "8px",
            width: 36,
            height: 36,
            color: Colors.grey._700,
            "& svg path": {stroke: "currentColor"},
            "&:hover": {backgroundColor: Colors.red._100, borderColor: Colors.red._300, color: Colors.red._700},
          }}
        >
          <Box component={IconTrash} sx={{width: 16, height: 16}} />
        </IconButton>
      </Box>
    </Container>
  );
};

export default ConditionRow;
